import * as React from 'react';
import styled from 'styled-components';
import { Checkbox } from 'antd';
import theme from 'theme';
import t from 'utils/translations';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ArticleListItem from 'components/common/ArticleListItem';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { FeedItem, PageResponse } from 'components/context/types';
import {
    ArticleList,
    ArticleListType,
    BusinessFeedItem,
    feedItemToBusinessFeedItem
} from 'components/common/FeedTypes';
import { apiGetAllPublicationsByUserGroup } from 'api/publications';
import { getNewsFilterEnumValue, NewsFilter } from 'utils/publications';
import Loading from 'components/common/Loading';

const Aside = styled.aside`
    background-color: ${theme.colors.white};
    border-radius: 4px;
    box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.09);
    flex: 1 0 330px;
    margin: 0 0 20px;
    height: 100%;

    @media (min-width: 700px) {
        &:first-of-type {
            margin-right: 20px;
        }
    }
`;

const ListHeader = styled.h3`
    align-items: center;
    border-bottom: 1px solid ${theme.colors.gray_lightest};
    border-left: 2px solid ${theme.colors.main};
    display: flex;
    font-family: ${theme.fonts.regular};
    font-size: 14px;
    justify-content: space-between;
    margin: 0;
    padding: 10px 10px 10px 25px;

    button {
        background-color: transparent;
        color: ${theme.colors.gray_darker};
        transition: color 0.2s;
        &:hover {
            color: ${theme.colors.main};
        }
    }
`;

const List = styled.ul`
    list-style-type: none;
    margin: 0;
    padding: 10px 10px 0;
`;

const Content = styled.div`
    padding: 10px;
    flex: 1 auto;
`;

const Filters = styled.div<{ show: boolean }>`
    background-color: ${theme.colors.main_light};
    border-bottom: 1px solid ${theme.colors.gray_lightest};
    max-height: ${p => (p.show ? '200px' : '0')};
    overflow: hidden;
    transition: max-height 0.35s, visibility 0.35s;
    visibility: ${p => (p.show ? 'visible' : 'hidden')};

    & > div {
        margin: 15px;
    }

    h5 {
        font-family: ${theme.fonts.medium};
        font-size: 14px;
        color: ${theme.colors.main};
    }

    .ant-checkbox-group-item {
        display: block;
    }
`;

const ShowMore = styled.button`
    background-color: transparent;
    border-top: 1px solid ${theme.colors.gray_lightest};
    color: ${theme.colors.main};
    font-family: ${theme.fonts.medium};
    padding: 5px 10px;
    text-align: right;
    width: 100%;
`;

export default function FrontPageArticleList({
    title,
    articleListType
}: ArticleList) {
    const [showFilters, setShowFilters] = React.useState(false);
    const [feeds, setFeeds] = React.useState<BusinessFeedItem[]>([]);
    const [errorMessage, setErrorMessage] = React.useState<String>();
    const [showMoreArticles, setShowMoreArticles] = React.useState<Boolean>(
        false
    );
    const [filters, setFilters] = React.useState<NewsFilter[]>([
        NewsFilter.TALENT_NEWS,
        NewsFilter.APPOINTMENTS,
        NewsFilter.JOBS
    ]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [total, setTotal] = React.useState<number>(0);
    const defaultFiltersChecked = filters;

    const options = [
        { label: t('NEWS'), value: NewsFilter.TALENT_NEWS },
        { label: t('APPOINTMENTS'), value: NewsFilter.APPOINTMENTS },
        { label: t('JOBS'), value: NewsFilter.JOBS }
        /* FIXME: Uncomment after stocknews have been defined. { label: t('STOCK_NEWS'), value: 'stockNews' },*/
        /* FIXME: Uncomment after pressNews have been defined. { label: t('PRESS_NEWS'), value: 'pressNews' }*/
    ];

    const onChange = (checked: CheckboxValueType[]) => {
        setFilters(checked.map(f => getNewsFilterEnumValue(f)));
    };

    const showMore = () => {
        setShowMoreArticles(!showMoreArticles);
    };
    const sourceToArticleListType = (source?: String) => {
        switch (source) {
            case 'Appointment':
                return ArticleListType.APPOINTMENT;
            case 'monster':
            case 'tepalvelut':
                return ArticleListType.JOBS;
            default:
                return articleListType;
        }
    };
    const filterToSourceString = (newsFilter: NewsFilter): string => {
        switch (newsFilter) {
            case NewsFilter.APPOINTMENTS:
                return 'appointments';
            case NewsFilter.TALENT_NEWS:
                return 'news';
            case NewsFilter.JOBS:
                return 'jobs';
            default:
                return '';
        }
    };

    const setPublications = (page: number, pageSize: number) => {
        setLoading(true);
        let sourceFilters: string[] = [];
        if (articleListType === ArticleListType.SOCIALMEDIA) {
            sourceFilters.push('social');
        } else {
            sourceFilters = filters.map(filterToSourceString);
        }

        apiGetAllPublicationsByUserGroup(page - 1, pageSize, sourceFilters)
            .then((feedResponse: PageResponse<FeedItem>) => {
                const businessFeed: BusinessFeedItem[] = feedResponse.content.map(
                    feedItemToBusinessFeedItem
                );
                setFeeds(businessFeed);
                setTotal(feedResponse.total || 0);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    React.useEffect(() => {
        setPublications(1, 10);
    }, [filters]);

    const toggleFilters = () => setShowFilters(!showFilters);
    const shownArticles = showMoreArticles ? feeds : feeds.slice(0, 5);

    return (
        <Aside>
            <ListHeader>
                {title}
                {articleListType !== ArticleListType.SOCIALMEDIA && (
                    <button onClick={toggleFilters}>
                        <FontAwesomeIcon icon={['far', 'cog']} />
                    </button>
                )}
            </ListHeader>
            {errorMessage && <p>{errorMessage}</p>}
            <Filters show={showFilters}>
                <div>
                    <h5>{t('CHOOSE_FEED_CONTENT')}</h5>
                    <Checkbox.Group
                        options={options}
                        defaultValue={defaultFiltersChecked}
                        onChange={onChange}
                    />
                </div>
            </Filters>
            <Content>
                {loading && <Loading fullScreen={false} height={320} />}
                {total === 0 && !loading && (
                    <p>{t('CONTENT_FOR_COMPANY_NOT_FOUND')}</p>
                )}
                <List>
                    {shownArticles.map(
                        (article: FeedItem) =>
                            article.id && (
                                <ArticleListItem
                                    key={article.id}
                                    {...article}
                                    articleListType={sourceToArticleListType(
                                        article.source
                                    )}
                                />
                            )
                    )}
                </List>
            </Content>
            <ShowMore onClick={showMore}>
                {(!showMoreArticles && `+ ${t('SHOW_MORE')}`) ||
                    `- ${t('SHOW_LESS')}`}
            </ShowMore>
        </Aside>
    );
}
